import { format } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { Box, Button, Icon } from '~/commonComponents';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import type { IShiftDetailsFormValues } from '~/components/ShiftRequestRedesign/ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';
import { UserProfession, UserSubProfession } from '~/enums/UserProfession';
import { useUserSelector } from '~/hooks';
import { useNavigationDetector } from '~/hooks/useNavigationDetector';
import { ShiftRequestHeader } from '../common';
import { DatesCard } from './DatesCard';
import { useSafeSelectDatesLocationState } from './useSafeLocationState';
import { useSelectDates } from './useSelectDates';
import { checkIfAnyHoliday, convertDatesToTimezone } from './utils';

export const SelectDatesScreen = () => {
  const navigate = useNavigate();
  const { setValue, getValues, formState } = useFormContext<IShiftDetailsFormValues>();
  const user = useUserSelector();
  const [showHolidayWarning, setShowHolidayWarning] = useState(false);

  const {
    professionType: navProfessionType,
    preselectedDate,
    professionals,
  } = useSafeSelectDatesLocationState();

  // Resolve profession type code to display name (e.g. 'RDH' -> 'Dental Hygienist')
  const resolvedProfessionType = navProfessionType
    ? UserProfession.getName(navProfessionType)
      || UserSubProfession.getName(navProfessionType)
      || navProfessionType
    : getValues('professionType') || '';

  // Set professionType in form on mount when coming from PostNewShiftsButton
  useEffect(() => {
    if (navProfessionType) {
      const resolved =
        UserProfession.getName(navProfessionType)
        || UserSubProfession.getName(navProfessionType)
        || navProfessionType;
      setValue('professionType', resolved);
    }
  }, [navProfessionType, setValue]);

  // Set invite field when professionals are passed from navigation state
  useEffect(() => {
    if (professionals?.length) {
      const ids: string[] = [];
      const names: Record<string, string> = {};

      professionals.forEach((p) => {
        if (typeof p === 'string') {
          ids.push(p);
        } else {
          ids.push(p.id);
          if (p.name) {
            names[p.id] = p.name;
          }
        }
      });

      setValue('invite', ids);
      if (Object.keys(names).length > 0) {
        setValue('inviteNames', names);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- Run once on mount: seed invite list from navigation state

  // Pre-select existing dates from form context when navigating back from ShiftDetails
  const existingDates = getValues('dates');

  const {
    selectedDates,
    handleCalendarSelect: hookCalendarSelect,
    handleRemoveDate,
    canContinue,
    tipVisible,
    nextButtonText,
  } = useSelectDates({ initialDate: preselectedDate, initialDates: existingDates });

  const handleCalendarSelect = useCallback(
    (dates: Date[] | undefined) => {
      if (!dates) {
        hookCalendarSelect([]);
        return;
      }
      hookCalendarSelect(dates.map((d) => format(d, 'yyyy-MM-dd')));
    },
    [hookCalendarSelect],
  );

  const hasChanges = selectedDates.length > 0 || formState.isDirty;
  const [showNavigationGuard, setShowNavigationGuard] = useState(false);

  const onNavigationAttempt = useCallback(() => {
    setShowNavigationGuard(true);
  }, []);

  const { allowNavigation, navigateToPending, clearPendingNavigation } = useNavigationDetector({
    when: hasChanges,
    onNavigationAttempt,
  });

  const proceedToShiftDetails = () => {
    allowNavigation();
    setValue('dates', selectedDates);
    setValue('professionType', resolvedProfessionType);
    navigate('/dashboard/multi-day/info');
  };

  const handleNext = () => {
    const dateStrings = selectedDates.map((d) => format(d, 'yyyy-MM-dd'));
    if (checkIfAnyHoliday(convertDatesToTimezone(dateStrings, user.time_zone))) {
      setShowHolidayWarning(true);
      return;
    }
    proceedToShiftDetails();
  };

  return (
    <ShiftRequestHeader currentStep={0} backUrl="/" maxWidth={1120}>
      <Box marginTop={32}>
        <DatesCard
          selectedDates={selectedDates}
          tipVisible={tipVisible}
          onCalendarSelect={handleCalendarSelect}
          onRemoveDate={handleRemoveDate}
        />
      </Box>

      <Button
        text={nextButtonText}
        disabled={!canContinue}
        onClick={handleNext}
        style={{ width: '100%', maxWidth: 296, margin: '36px auto 0', display: 'block' }}
      />

      {showHolidayWarning && (
        // @ts-expect-error -- ConfirmPopup still written in JavaScript
        <ConfirmPopup
          title="Major U.S Holiday Conflict"
          description="Looks like you are trying to post a shift on a Major U.S. Holiday. Please make sure your office will be open on this date!"
          leftButtonText="Go Back"
          leftButtonAction={() => setShowHolidayWarning(false)}
          rightButtonText="Continue"
          rightButtonAction={() => {
            setShowHolidayWarning(false);
            proceedToShiftDetails();
          }}
          closePopup={() => setShowHolidayWarning(false)}
          dataTestId="holiday-warning-popup"
        />
      )}

      {showNavigationGuard && (
        // @ts-expect-error -- ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={() => (
            <div style={{ marginBottom: 20 }}>
              <Icon name="warning-outline" size={80} />
            </div>
          )}
          title="Are You Sure You Want to Leave?"
          description="Your shifts haven't been posted yet. Leaving now means the progress made to your request won't be saved."
          leftButtonText="Go Back"
          leftButtonAction={() => {
            clearPendingNavigation();
            setShowNavigationGuard(false);
          }}
          rightButtonText="Leave Anyway"
          rightButtonAction={navigateToPending}
          closePopup={() => {
            clearPendingNavigation();
            setShowNavigationGuard(false);
          }}
          dataTestId="navigation-guard-popup"
        />
      )}
    </ShiftRequestHeader>
  );
};
